import { useCallback, useState, type ReactNode } from 'react';
import { hasScope } from '@nyce/admin-kit/api';
import { Button } from '@nyce/admin-kit/ui';
import { ErrorBox } from '../../components/common';
import { useHubLogin } from '../../session';
import { TargetSignIn } from '../../identity/TargetShell';
import { targetList, useTargets } from '../../identity/session';
import { SecretReveal } from '@nyce/admin-kit/console';

/** Run one write of a step: busy flag, error text, then `after` (usually the wizard's reload). */
export function useAction(after?: () => unknown) {
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const run = useCallback(
    async <T,>(name: string, fn: () => Promise<T>): Promise<T | undefined> => {
      setBusy(name);
      setError(null);
      try {
        const r = await fn();
        await after?.();
        return r;
      } catch (e) {
        setError((e as Error).message);
        return undefined;
      } finally {
        setBusy(null);
      }
    },
    [after]
  );
  return { run, busy, error, setError };
}

/** A step's buttons and its last error. */
export function StepActions({ error, children }: { error?: string | null; children: ReactNode }) {
  return (
    <>
      <ErrorBox error={error} />
      <div className="nyk-row-actions ob-actions">{children}</div>
    </>
  );
}

/** What a step found or will do, as label / value rows. */
export function Facts({ rows }: { rows: [ReactNode, ReactNode][] }) {
  return (
    <table className="nyk-kv ob-facts">
      <tbody>
        {rows.map(([k, v], i) => (
          <tr key={i}>
            <th>{k}</th>
            <td>{v}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export function Note({ tone = 'info', children }: { tone?: 'info' | 'good' | 'warn'; children: ReactNode }) {
  return (
    <div className={`ob-note ob-note-${tone}`} data-tone={tone}>
      {children}
    </div>
  );
}

/** A secret shown with a copy button (it also goes into the generated files). */
export function SecretValue({ label, value }: { label: string; value: string }) {
  return <SecretReveal className="ob-secret" label={label} value={value} data-testid="secret" />;
}

/** Children only when the operator is signed in to AUTH `target`; else that environment's sign-in form. */
export function NeedsSession({ target, children }: { target: string; children: ReactNode }) {
  const { signedIn } = useTargets();
  const info = targetList().find((t) => t.id === target);
  if (signedIn(target) || !info) return <>{children}</>;
  return <TargetSignIn target={info} />;
}

/** Whether the hub login holds `scope` (wildcards and write ⇒ read honoured). */
export function useScope(scope: string): boolean {
  return hasScope(useHubLogin()?.scopes, scope);
}

/** A step blocked by a missing hub scope says so instead of offering buttons that answer 403. */
export function NeedsScope({ scope, children }: { scope: string; children: ReactNode }) {
  return useScope(scope) ? (
    <>{children}</>
  ) : (
    <Note tone="warn">
      Your hub account lacks <code>{scope}</code>: someone with it has to do this step.
    </Note>
  );
}

/** A link-style button that opens the full page of a thing the wizard made, for settings the wizard leaves out. */
export function MoreLink({ to, children }: { to: string; children: ReactNode }) {
  return (
    <a className="nyk-link ob-more" href={to} target="_blank" rel="noreferrer">
      {children} ↗
    </a>
  );
}

export { Button };
